import { CommonModule } from '@angular/common';
import { Component, inject, signal, viewChild } from '@angular/core';
import { DxButtonModule, DxFormComponent, DxFormModule } from 'devextreme-angular';
import { ShamShamLogoFullIcon } from 'sham-components/icons';
import { RequestPasswordResetManager } from './request-password-reset-manager';
import { Router } from '@angular/router';
import {
  RequestOtpPayload,
  VerifyPasswordResetOtpPayload,
} from 'sham-components/shared/models/auth';

@Component({
  selector: 'app-request-password-reset',
  imports: [CommonModule, DxFormModule, DxButtonModule, ShamShamLogoFullIcon],
  providers: [RequestPasswordResetManager],
  templateUrl: './request-password-reset.html',
})
export class RequestPasswordResetComponent {
  private readonly router = inject(Router);
  readonly manager = inject(RequestPasswordResetManager);

  readonly step = signal<1 | 2>(1);
  readonly loading = signal(false);
  readonly submitError = signal('');

  readonly requestOtpFormData = signal<RequestOtpPayload>({
    email: '',
  });

  readonly verifyOtpFormData = signal<VerifyPasswordResetOtpPayload>({
    email: '',
    otp: '',
  });

  readonly form = viewChild.required(DxFormComponent);

  readonly emailEditorOptions = {
    mode: 'email',
    placeholder: 'name@example.com',
    buttons: [
      {
        name: 'email',
        location: 'before',
        options: {
          icon: 'email',
          stylingMode: 'text',
          focusStateEnabled: false,
          elementAttr: {
            class: 'cursor-auto custom-icon-color pointer-events-none',
          },
        },
      },
    ],
  };

  readonly otpEditorOptions = {
    placeholder: 'Enter OTP code',
    maxLength: 6,
    buttons: [
      {
        name: 'key',
        location: 'before',
        options: {
          icon: 'key',
          stylingMode: 'text',
          focusStateEnabled: false,
          elementAttr: {
            class: 'cursor-auto custom-icon-color pointer-events-none',
          },
        },
      },
    ],
  };

  async onRequestOtp() {
    const validation = this.form().instance.validate();

    if (!validation.isValid) {
      return;
    }

    this.loading.set(true);
    this.submitError.set('');

    await this.manager.requestOtp(
      this.requestOtpFormData(),
      this.submitError,
      this.loading,
    );

    if (!this.submitError()) {
      this.verifyOtpFormData.set({
        email: this.requestOtpFormData().email,
        otp: '',
      });

      this.step.set(2);
    }
  }

  async onVerifyOtp() {
    const validation = this.form().instance.validate();

    if (!validation.isValid) {
      return;
    }

    this.loading.set(true);
    this.submitError.set('');

    const resetToken = await this.manager.verifyOtp(
      this.verifyOtpFormData(),
      this.submitError,
      this.loading,
    );

    if (resetToken) {
      await this.router.navigate(['/reset-password'], {
        queryParams: {
          token: resetToken,
        },
      });
    }
  }

  backToEmailStep() {
    this.submitError.set('');
    this.step.set(1);
  }
}
